"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Activity, CheckCircle2, KeyRound, LoaderCircle, Play, RadioTower, RefreshCw, ShieldCheck, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GithubPemCredentialInput } from "./github-pem-credential-input";
import { Textarea } from "@/components/ui/textarea";

type Overview = {
  provider?: {
    adapters?: any[];
    credential_references?: any[];
    incidents?: any[];
    verification_queue?: any[];
    events?: any[];
    deployment_fabric?: any;
  };
  central_management?: any;
  access?: { provider_execute?: boolean };
};

function tone(value?: string | null) {
  const state = String(value || "").toUpperCase();
  if (["CONNECTED","HEALTHY","ACTIVE","AUTHORIZED","VERIFIED","SUCCEEDED","RESOLVED","IN_SYNC","FRESH"].includes(state)) return "bg-emerald-400/10 text-emerald-600 dark:text-emerald-300";
  if (["FAILED","OUTAGE","CRITICAL","EXPIRED","DRIFT"].includes(state)) return "bg-red-400/10 text-red-600 dark:text-red-300";
  if (["AUTH_REQUIRED","MISSING","WAITING","DEGRADED","OPEN","ACKNOWLEDGED","INSUFFICIENT_SCOPE","STALE"].includes(state)) return "bg-amber-400/10 text-amber-700 dark:text-amber-200";
  return "bg-foreground/[0.06] text-foreground/55";
}

const inputClass = "h-9 border-0 bg-background/70 text-xs shadow-none";

export function ProviderControlPanel({ supabase, isOwner }: { supabase: SupabaseClient | null; isOwner: boolean }) {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reason, setReason] = useState("Owner secure native-provider activation.");

  const [github, setGithub] = useState({
    app_id: "", installation_id: "", private_key: "",
    repository: "RUNYIAWARAFILMS/runyi-movie-engine-android",
    branch: "main", webhook_secret: "",
  });
  const [githubPemName, setGithubPemName] = useState<string | null>(null);
  const [cloudflare, setCloudflare] = useState({
    account_id: "", api_token: "", pages_project: "runyi-movie-creative-engine",
  });
  const [firebase, setFirebase] = useState({
    project_id: "", service_account_json: "",
  });
  const [firebaseJsonName, setFirebaseJsonName] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !isOwner) return;
    setBusy("refresh");
    try {
      const result = await supabase.functions.invoke("runyi-control-tower", {
        body: { operation: "provider_overview", limit: 50 },
      });
      if (result.error) throw result.error;
      if (result.data?.error) throw new Error(String(result.data.error));
      setOverview((result.data || null) as Overview | null);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not load provider operations.");
    } finally {
      setBusy(null);
    }
  }, [isOwner, supabase]);

  useEffect(() => {
    if (!isOwner) return;
    const query = new URLSearchParams(window.location.search);
    const oauthState = query.get("runyi_provider_oauth");
    const oauthMessage = query.get("runyi_provider_oauth_message");
    if (oauthState) {
      setNotice(oauthState === "connected"
        ? (oauthMessage && oauthMessage.includes("SANDBOX SUPABASE") ? oauthMessage : "Secure central Supabase authorization is ready for provider credential activation.")
        : (oauthMessage || "Secure provider authorization was not completed."));
      query.delete("runyi_provider_oauth");
      query.delete("runyi_provider_oauth_message");
      const next = query.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (next ? "?" + next : "") + window.location.hash);
    }
    void load();
  }, [isOwner, load]);

  const adapters = useMemo(
    () => (overview?.provider?.adapters || []).filter((item: any) =>
      ["github.server.app","cloudflare.server.api","firebase.server.admin"].includes(item.adapter_key)
    ),
    [overview],
  );
  const refs = overview?.provider?.credential_references || [];
  const incidents = overview?.provider?.incidents || [];
  const verification = overview?.provider?.verification_queue || [];
  const events = overview?.provider?.events || [];
  const fabric = overview?.provider?.deployment_fabric || {};
  const fabricProviders = fabric?.providers || [];
  const provenance = fabric?.deployment_provenance || [];
  const correlation = fabric?.correlation?.github_cloudflare || {};
  const canExecute = overview?.access?.provider_execute === true;

  async function refreshFabric() {
    if (!supabase) return;
    if (!canExecute) {
      await load();
      return;
    }
    setBusy("fabric-refresh");
    setNotice(null);
    try {
      const result = await supabase.functions.invoke("runyi-control-tower", {
        body: {
          operation: "provider_refresh_fabric",
          reason: "Owner requested live multi-provider deployment-fabric refresh.",
        },
      });
      if (result.error) throw result.error;
      if (result.data?.error) throw new Error(String(result.data.error));
      const failed = Object.keys(result.data?.errors || {});
      setNotice(
        failed.length === 0
          ? "Deployment fabric refreshed across GitHub, Cloudflare, Firebase and Supabase."
          : "Deployment fabric refreshed with partial provider errors: " + failed.join(", ") + ".",
      );
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not refresh deployment fabric.");
    } finally {
      setBusy(null);
    }
  }

  async function authorizeSecureSetup() {
    if (!supabase) return;
    setBusy("oauth");
    setNotice(null);
    try {
      const result = await supabase.functions.invoke("runyi-provider-oauth-start", { body: {} });
      if (result.error) throw result.error;
      if (result.data?.error) throw new Error(String(result.data.error));
      if (!result.data?.authorization_url) throw new Error("Secure Supabase authorization URL was not returned.");
      window.location.assign(String(result.data.authorization_url));
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not start secure provider setup.");
      setBusy(null);
    }
  }

  // SANDBOX SUPABASE: the second central connection CENTRAL_SUPABASE_SANDBOX_ORG (sandbox org only; never CENTRAL_SUPABASE).
  async function authorizeSandboxOrg() {
    if (!supabase) return;
    setBusy("oauth-sandbox-org");
    setNotice(null);
    try {
      const result = await supabase.functions.invoke("runyi-provider-oauth-start", { body: { connection_key: "CENTRAL_SUPABASE_SANDBOX_ORG" } });
      if (result.error) throw result.error;
      if (result.data?.error) throw new Error(String(result.data.error));
      if (!result.data?.authorization_url) throw new Error("SANDBOX SUPABASE authorization URL was not returned.");
      window.location.assign(String(result.data.authorization_url));
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not start SANDBOX SUPABASE authorization.");
      setBusy(null);
    }
  }

  async function loadFirebaseServiceAccountFile(file: File | null) {
    if (!file) return;
    setNotice(null);

    if (file.size <= 0 || file.size > 256 * 1024) {
      setFirebaseJsonName(null);
      setFirebase({ project_id: "", service_account_json: "" });
      setNotice("The Firebase service-account file must be a non-empty JSON file smaller than 256 KB.");
      return;
    }

    try {
      const text = (await file.text()).replace(/^\uFEFF/, "").trim();
      const parsed = JSON.parse(text);
      const projectId = String(parsed?.project_id || "").trim();
      const clientEmail = String(parsed?.client_email || "").trim();
      const privateKey = String(parsed?.private_key || "").trim();

      if (!projectId || !clientEmail || !privateKey) {
        throw new Error("That JSON is not a complete Firebase service-account credential.");
      }
      if (
        !privateKey.includes("-----BEGIN PRIVATE KEY-----") &&
        !privateKey.includes("-----BEGIN RSA PRIVATE KEY-----")
      ) {
        throw new Error("The Firebase service-account private key is not a supported PEM.");
      }

      setFirebase({ project_id: projectId, service_account_json: text });
      setFirebaseJsonName(file.name || "firebase-service-account.json");
      setNotice("Firebase service account loaded. Project ID " + projectId + " was detected automatically. The credential is kept only in memory until secure activation.");
    } catch (error) {
      setFirebaseJsonName(null);
      setFirebase({ project_id: "", service_account_json: "" });
      setNotice(error instanceof Error ? error.message : "Could not read the Firebase service-account JSON file.");
    }
  }

  async function activateProvider(provider: "GITHUB" | "CLOUDFLARE" | "FIREBASE") {
    if (!supabase) return;
    if (!reason.trim()) {
      setNotice("Enter a credential activation reason first.");
      return;
    }
    const credentials = provider === "GITHUB" ? { ...github } : provider === "CLOUDFLARE" ? { ...cloudflare } : { ...firebase };
    setBusy("activate-" + provider);
    setNotice(null);
    try {
      const result = await supabase.functions.invoke("runyi-provider-credentials", {
        body: { operation: "activate", provider_key: provider, credentials, reason: reason.trim() },
      });
      if (result.error) throw result.error;
      if (result.data?.error) throw new Error(String(result.data.error));
      setNotice(provider + " secure activation finished with state " + String(result.data?.status || "UPDATED") + ".");
      if (provider === "GITHUB") {
        setGithub({
          app_id: "", installation_id: "", private_key: "",
          repository: "RUNYIAWARAFILMS/runyi-movie-engine-android", branch: "main", webhook_secret: "",
        });
        setGithubPemName(null);
      }
      if (provider === "CLOUDFLARE") setCloudflare({
        account_id: "", api_token: "", pages_project: "runyi-movie-creative-engine",
      });
      if (provider === "FIREBASE") setFirebase({ project_id: "", service_account_json: "" });
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : provider + " activation failed.");
    } finally {
      if (provider === "GITHUB") setGithub((v) => ({ ...v, private_key: "", webhook_secret: "" }));
      if (provider === "CLOUDFLARE") setCloudflare((v) => ({ ...v, api_token: "" }));
      if (provider === "FIREBASE") setFirebase((v) => ({ ...v, service_account_json: "" }));
      setBusy(null);
    }
  }

  async function probeStoredProvider(provider: "GITHUB" | "CLOUDFLARE" | "FIREBASE") {
    if (!supabase) return;
    setBusy("probe-" + provider);
    setNotice(null);
    try {
      const result = await supabase.functions.invoke("runyi-provider-credentials", {
        body: {
          operation: "probe",
          provider_key: provider,
          reason: "Owner requested stored-provider credential verification.",
        },
      });
      if (result.error) throw result.error;
      if (result.data?.error) throw new Error(String(result.data.error));
      setNotice(provider + " stored credentials verified with state " + String(result.data?.status || "UPDATED") + ".");
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : provider + " credential verification failed.");
    } finally {
      setBusy(null);
    }
  }

  async function runVerifier() {
    if (!supabase || !canExecute) return;
    setBusy("verify");
    try {
      const result = await supabase.functions.invoke("runyi-control-tower", {
        body: { operation: "provider_verify_pending", limit: 10, reason: "Owner requested provider-operation verification." },
      });
      if (result.error) throw result.error;
      if (result.data?.error) throw new Error(String(result.data.error));
      setNotice("Provider verifier processed " + String(result.data?.verification?.claimed || 0) + " due jobs.");
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Provider verification failed.");
    } finally {
      setBusy(null);
    }
  }

  async function incidentAction(id: string, operation: "provider_incident_ack" | "provider_incident_resolve") {
    if (!supabase || !canExecute) return;
    setBusy(operation + "-" + id);
    try {
      const result = await supabase.functions.invoke("runyi-control-tower", {
        body: { operation, incident_id: id, reason: "Owner provider incident action from Enterprise Control Tower." },
      });
      if (result.error) throw result.error;
      if (result.data?.error) throw new Error(String(result.data.error));
      setNotice(operation === "provider_incident_ack" ? "Provider incident acknowledged." : "Provider incident resolved.");
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Provider incident update failed.");
    } finally {
      setBusy(null);
    }
  }

  if (!isOwner) return null;

  return (
    <div className="space-y-3 rounded-2xl bg-foreground/[0.022] p-3">
      <div className="flex items-start gap-2.5">
        <div className="grid size-6 shrink-0 place-items-center rounded-md bg-primary/[0.08] text-primary">
          <RadioTower className="size-3" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold">Enterprise Providers & DevOps</p>
          <p className="mt-1 text-[11px] leading-4 text-foreground/42">
            Native credential activation, protected operations, normalized events, verification and incidents.
          </p>
        </div>
        <Button variant="ghost" size="icon" className="size-8" onClick={() => void refreshFabric()} disabled={Boolean(busy)}>
          {busy === "fabric-refresh" || busy === "refresh" ? <LoaderCircle className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
        </Button>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        {adapters.map((adapter: any) => (
          <div key={adapter.adapter_key} className="rounded-xl bg-background/55 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold">{adapter.provider_key}</p>
              <Badge className={tone(adapter.status)}>{adapter.status || "UNKNOWN"}</Badge>
            </div>
            <p className="mt-1 truncate font-mono text-[9px] text-foreground/35">{adapter.adapter_key}</p>
            <p className="mt-2 text-[9px] text-foreground/42">Health: {adapter.health_state || "UNKNOWN"}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl bg-background/45 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5">
              <Activity className="size-3.5 text-primary" />
              <p className="text-[11px] font-semibold">Unified deployment fabric</p>
            </div>
            <p className="mt-1 text-[9px] text-foreground/38">
              GitHub source → Cloudflare production → Firebase services → Supabase control plane.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge className={tone(correlation?.state)}>{correlation?.state || "UNKNOWN"}</Badge>
            <Badge className={tone(Number(fabric?.health_summary?.healthy || 0) === Number(fabric?.health_summary?.total || 0) ? "HEALTHY" : "DEGRADED")}>
              {String(fabric?.health_summary?.healthy || 0)}/{String(fabric?.health_summary?.total || 0)} healthy
            </Badge>
          </div>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="rounded-lg bg-foreground/[0.025] px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-semibold">GitHub source</p>
              <Badge className={tone(fabric?.github?.health_state)}>{fabric?.github?.health_state || "UNKNOWN"}</Badge>
            </div>
            <p className="mt-1 truncate font-mono text-[9px] text-foreground/45">
              {fabric?.github?.head_commit ? String(fabric.github.head_commit).slice(0, 12) : "No commit observed"}
            </p>
            <p className="mt-1 truncate text-[9px] text-foreground/32">{fabric?.github?.repository || "Repository unavailable"}</p>
          </div>

          <div className="rounded-lg bg-foreground/[0.025] px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-semibold">Cloudflare production</p>
              <Badge className={tone(fabric?.cloudflare?.health_state)}>{fabric?.cloudflare?.stage_status || fabric?.cloudflare?.health_state || "UNKNOWN"}</Badge>
            </div>
            <p className="mt-1 truncate font-mono text-[9px] text-foreground/45">
              {fabric?.cloudflare?.commit_sha ? String(fabric.cloudflare.commit_sha).slice(0, 12) : "No deployment commit"}
            </p>
            <p className="mt-1 truncate text-[9px] text-foreground/32">{fabric?.cloudflare?.project || "Pages project unavailable"}</p>
          </div>

          <div className="rounded-lg bg-foreground/[0.025] px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-semibold">Firebase</p>
              <Badge className={tone(fabric?.firebase?.health_state)}>{fabric?.firebase?.health_state || "UNKNOWN"}</Badge>
            </div>
            <p className="mt-1 truncate font-mono text-[9px] text-foreground/45">{fabric?.firebase?.project_id || "No project observed"}</p>
            <p className="mt-1 truncate text-[9px] text-foreground/32">{fabric?.firebase?.hosting_site || "Hosting not observed"}</p>
          </div>

          <div className="rounded-lg bg-foreground/[0.025] px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-semibold">Supabase control plane</p>
              <Badge className={tone(fabric?.supabase?.status)}>{fabric?.supabase?.status || "UNKNOWN"}</Badge>
            </div>
            <p className="mt-1 truncate font-mono text-[9px] text-foreground/45">{fabric?.supabase?.project_ref || "No project ref"}</p>
            <p className="mt-1 text-[9px] text-foreground/32">Central governance authority</p>
          </div>
        </div>

        <div className="mt-2 rounded-lg bg-foreground/[0.025] px-2.5 py-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold">GitHub → Cloudflare correlation</p>
            <Badge className={tone(correlation?.state)}>{correlation?.state || "UNKNOWN"}</Badge>
          </div>
          <p className="mt-1 font-mono text-[9px] text-foreground/42">
            source {correlation?.github_commit ? String(correlation.github_commit).slice(0, 10) : "—"} · deployed {correlation?.cloudflare_commit ? String(correlation.cloudflare_commit).slice(0, 10) : "—"}
          </p>
        </div>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {fabricProviders.map((item: any) => (
            <Badge key={item.adapter_key} className={tone(item.freshness)}>
              {item.provider_key} {item.freshness || "UNKNOWN"}
            </Badge>
          ))}
        </div>

        {provenance.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-foreground/32">Recent provenance</p>
            {provenance.slice(0, 4).map((item: any) => (
              <div key={item.deployment_id} className="flex items-center justify-between gap-2 rounded-lg bg-foreground/[0.025] px-2.5 py-2">
                <div className="min-w-0">
                  <p className="truncate text-[9px] font-medium">{item.component_name}</p>
                  <p className="mt-0.5 truncate font-mono text-[8px] text-foreground/32">
                    {item.git_commit_sha ? String(item.git_commit_sha).slice(0, 12) : "no-commit"} · {item.component_type}
                  </p>
                </div>
                <Badge className={tone(item.status)}>{item.status || "OBSERVED"}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl bg-background/45 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5">
              <KeyRound className="size-3.5 text-primary" />
              <p className="text-[11px] font-semibold">Secure native credential setup</p>
            </div>
            <p className="mt-1 text-[10px] text-foreground/42">
              Central Supabase management: {overview?.central_management?.status || "NOT CONNECTED"}.
              Values are streamed directly into Edge Function Secrets.
            </p>
          </div>
          <Button size="sm" variant="outline" className="h-8 text-[10px]" onClick={() => void authorizeSecureSetup()} disabled={Boolean(busy)}>
            {busy === "oauth" ? <LoaderCircle className="mr-1.5 size-3 animate-spin" /> : <ShieldCheck className="mr-1.5 size-3" />}
            Authorize secure setup
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-[10px]" onClick={() => void authorizeSandboxOrg()} disabled={Boolean(busy)}>
            {busy === "oauth-sandbox-org" ? <LoaderCircle className="mr-1.5 size-3 animate-spin" /> : <ShieldCheck className="mr-1.5 size-3" />}
            Authorize SANDBOX SUPABASE
          </Button>
        </div>

        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Credential activation reason" className={"mt-3 " + inputClass} autoComplete="off" />
        {notice && (
          <p className="mt-2 rounded-lg bg-primary/[0.06] px-3 py-2 text-[10px] leading-4 text-foreground/65">
            {notice}
          </p>
        )}

        <details className="mt-3 rounded-lg bg-foreground/[0.025] p-2.5">
          <summary className="cursor-pointer text-[11px] font-semibold">GitHub native App</summary>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Input value={github.app_id} onChange={(e) => setGithub(v => ({...v,app_id:e.target.value}))} placeholder="GitHub App ID" className={inputClass} autoComplete="off" />
            <Input value={github.installation_id} onChange={(e) => setGithub(v => ({...v,installation_id:e.target.value}))} placeholder="Installation ID" className={inputClass} autoComplete="off" />
            <Input value={github.repository} onChange={(e) => setGithub(v => ({...v,repository:e.target.value}))} placeholder="owner/repository" className={inputClass} autoComplete="off" />
            <Input value={github.branch} onChange={(e) => setGithub(v => ({...v,branch:e.target.value}))} placeholder="main branch" className={inputClass} autoComplete="off" />
          </div>
          <div className="mt-2 rounded-lg bg-background/55 p-2.5">
            <p className="text-[10px] font-semibold">GitHub App private key</p>
            <p className="mt-1 text-[9px] leading-4 text-foreground/38">
              Recommended on Android: choose the .pem file downloaded directly from GitHub. RUNYI reads it in memory and never stores it in browser storage.
            </p>
            <GithubPemCredentialInput
              selectedName={githubPemName}
              onSelectedNameChange={setGithubPemName}
              onPemLoaded={(pem)=>setGithub((value)=>({...value,private_key:pem}))}
              onError={setNotice}
              className={"mt-2 " + inputClass}
            />
          </div>
          <Textarea
            value={github.private_key}
            onChange={(e) => {
              setGithubPemName(null);
              setGithub(v => ({...v,private_key:e.target.value}));
            }}
            placeholder="Or paste the full GitHub App private key PEM"
            className="mt-2 min-h-28 border-0 bg-background/70 font-mono text-[10px] shadow-none"
            autoComplete="off"
            spellCheck={false}
          />
          <Input type="password" value={github.webhook_secret} onChange={(e) => setGithub(v => ({...v,webhook_secret:e.target.value}))} placeholder="Optional GitHub webhook secret" className={"mt-2 " + inputClass} autoComplete="new-password" />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" className="h-8 text-[10px]" onClick={() => void activateProvider("GITHUB")} disabled={Boolean(busy)}>
              {busy === "activate-GITHUB" ? <LoaderCircle className="mr-1.5 size-3 animate-spin" /> : <KeyRound className="mr-1.5 size-3" />} Activate GitHub
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-[10px]" onClick={() => void probeStoredProvider("GITHUB")} disabled={Boolean(busy)}>
              {busy === "probe-GITHUB" ? <LoaderCircle className="mr-1.5 size-3 animate-spin" /> : <RefreshCw className="mr-1.5 size-3" />} Verify stored credentials
            </Button>
          </div>
        </details>

        <details className="mt-2 rounded-lg bg-foreground/[0.025] p-2.5">
          <summary className="cursor-pointer text-[11px] font-semibold">Cloudflare server API</summary>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Input value={cloudflare.account_id} onChange={(e) => setCloudflare(v => ({...v,account_id:e.target.value}))} placeholder="Cloudflare account ID" className={inputClass} autoComplete="off" />
            <Input value={cloudflare.pages_project} onChange={(e) => setCloudflare(v => ({...v,pages_project:e.target.value}))} placeholder="Pages project" className={inputClass} autoComplete="off" />
          </div>
          <Input type="password" value={cloudflare.api_token} onChange={(e) => setCloudflare(v => ({...v,api_token:e.target.value}))} placeholder="Cloudflare API token" className={"mt-2 " + inputClass} autoComplete="new-password" />
          <Button size="sm" className="mt-2 h-8 text-[10px]" onClick={() => void activateProvider("CLOUDFLARE")} disabled={Boolean(busy)}>
            {busy === "activate-CLOUDFLARE" ? <LoaderCircle className="mr-1.5 size-3 animate-spin" /> : <KeyRound className="mr-1.5 size-3" />} Activate Cloudflare
          </Button>
        </details>

        <details className="mt-2 rounded-lg bg-foreground/[0.025] p-2.5">
          <summary className="cursor-pointer text-[11px] font-semibold">Firebase Admin</summary>
          <div className="mt-3 rounded-lg bg-background/55 p-2.5">
            <p className="text-[10px] font-semibold">Firebase service-account JSON</p>
            <p className="mt-1 text-[9px] leading-4 text-foreground/38">
              Choose the JSON file downloaded from Firebase. RUNYI reads it in memory, detects project_id automatically, and streams it securely into Edge Function Secrets.
            </p>
            <Input
              type="file"
              className={"mt-2 " + inputClass}
              onChange={(e) => void loadFirebaseServiceAccountFile(e.target.files?.[0] ?? null)}
            />
            <p className="mt-1 text-[9px] text-foreground/38">
              {firebaseJsonName ? "Loaded: " + firebaseJsonName : "No Firebase service-account file selected."}
            </p>
          </div>
          <Input
            value={firebase.project_id}
            readOnly
            placeholder="Firebase project ID — detected from JSON"
            className={"mt-2 " + inputClass}
            autoComplete="off"
          />
          <Textarea
            value={firebase.service_account_json}
            onChange={(e) => {
              setFirebaseJsonName(null);
              const raw=e.target.value;
              let detected="";
              try{detected=String(JSON.parse(raw)?.project_id||"").trim();}catch{}
              setFirebase({ project_id: detected, service_account_json: raw });
            }}
            placeholder="Or paste the full Firebase service-account JSON"
            className="mt-2 min-h-32 border-0 bg-background/70 font-mono text-[10px] shadow-none"
            autoComplete="off"
            spellCheck={false}
          />
          <Button size="sm" className="mt-2 h-8 text-[10px]" onClick={() => void activateProvider("FIREBASE")} disabled={Boolean(busy)}>
            {busy === "activate-FIREBASE" ? <LoaderCircle className="mr-1.5 size-3 animate-spin" /> : <KeyRound className="mr-1.5 size-3" />} Activate Firebase
          </Button>
        </details>

        <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
          {refs.map((item: any) => (
            <div key={item.secret_key} className="flex items-center justify-between gap-2 rounded-lg bg-foreground/[0.025] px-2.5 py-2">
              <div className="min-w-0">
                <p className="truncate font-mono text-[9px] text-foreground/48">{item.secret_key}</p>
                <p className="mt-0.5 text-[9px] text-foreground/30">{item.provider} · value stored here: no</p>
              </div>
              <Badge className={tone(item.status)}>{item.status}</Badge>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-background/45 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5"><Activity className="size-3.5 text-primary" /><p className="text-[11px] font-semibold">Async verification</p></div>
          <Button size="sm" variant="outline" className="h-8 text-[10px]" onClick={() => void runVerifier()} disabled={!canExecute || Boolean(busy)}>
            {busy === "verify" ? <LoaderCircle className="mr-1.5 size-3 animate-spin" /> : <Play className="mr-1.5 size-3" />} Run due verification
          </Button>
        </div>
        <div className="mt-2 max-h-44 space-y-1.5 overflow-y-auto">
          {verification.length === 0 && <p className="py-3 text-center text-[10px] text-foreground/35">No verification jobs.</p>}
          {verification.map((job: any) => (
            <div key={job.verification_id} className="rounded-lg bg-foreground/[0.025] px-2.5 py-2">
              <div className="flex items-center justify-between gap-2"><p className="truncate text-[10px] font-medium">{job.action_key}</p><Badge className={tone(job.status)}>{job.status}</Badge></div>
              <p className="mt-1 text-[9px] text-foreground/35">{job.provider_key} · attempts {job.attempts}/{job.max_attempts} · {job.verification_profile}</p>
              {job.last_error && <p className="mt-1 line-clamp-2 text-[9px] text-red-500/80">{job.last_error}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-background/45 p-3">
        <div className="flex items-center gap-1.5"><TriangleAlert className="size-3.5 text-amber-500" /><p className="text-[11px] font-semibold">Provider incidents</p></div>
        <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto">
          {incidents.length === 0 && <p className="py-3 text-center text-[10px] text-foreground/35">No provider incidents.</p>}
          {incidents.map((incident: any) => (
            <div key={incident.incident_id} className="rounded-lg bg-foreground/[0.025] px-2.5 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><p className="text-[10px] font-medium">{incident.summary}</p><p className="mt-1 text-[9px] text-foreground/35">{incident.provider_key} · {incident.incident_type}</p></div>
                <div className="flex gap-1"><Badge className={tone(incident.severity)}>{incident.severity}</Badge><Badge className={tone(incident.state)}>{incident.state}</Badge></div>
              </div>
              {incident.rollback_recommended && <p className="mt-1.5 text-[9px] text-amber-700 dark:text-amber-200">Rollback recommended · {incident.recommendation || "Review the last healthy provider state."}</p>}
              {canExecute && !["RESOLVED","DISMISSED"].includes(incident.state) && (
                <div className="mt-2 flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[9px]" onClick={() => void incidentAction(incident.incident_id,"provider_incident_ack")} disabled={Boolean(busy)}>Acknowledge</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[9px]" onClick={() => void incidentAction(incident.incident_id,"provider_incident_resolve")} disabled={Boolean(busy)}><CheckCircle2 className="mr-1 size-3" />Resolve</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl bg-background/45 p-3">
        <div className="flex items-center gap-1.5"><RadioTower className="size-3.5 text-primary" /><p className="text-[11px] font-semibold">Recent normalized events</p></div>
        <div className="mt-2 max-h-44 space-y-1.5 overflow-y-auto">
          {events.length === 0 && <p className="py-3 text-center text-[10px] text-foreground/35">No provider events yet.</p>}
          {events.slice(0,30).map((event: any) => (
            <div key={event.event_id} className="flex items-center justify-between gap-2 rounded-lg bg-foreground/[0.025] px-2.5 py-2">
              <div className="min-w-0"><p className="truncate text-[10px] font-medium">{event.event_type}</p><p className="mt-0.5 truncate text-[9px] text-foreground/35">{event.provider_key}</p></div>
              <Badge className={tone(event.event_state)}>{event.event_state}</Badge>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
